import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, Inbox, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

type Channel = 'telegram' | 'whatsapp';
type Period = 'all' | '7' | '30' | '90';

interface ChannelMessage {
  id: number;
  telegram_user_id?: number;
  telegram_username?: string | null;
  whatsapp_user_id?: string;
  display_name?: string | null;
  chat_id: string | number;
  chat_type: string;
  chat_name?: string | null;
  user_message: string;
  assistant_response: string | null;
  createdAt: string;
  response_time_ms: number | null;
}

interface Props {
  channel: Channel;
  standalone?: boolean;
}

const AdminChannelMessages = ({ channel, standalone = false }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<Period>('all');
  const [applied, setApplied] = useState({ search: '', period: 'all' as Period });
  const [messageToDelete, setMessageToDelete] = useState<ChannelMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const pageSize = 20;
  const endpoint = `/api/${channel}-messages`;
  const label = channel === 'telegram' ? 'Telegram' : 'WhatsApp';

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const body: Record<string, string | number> = { operation: 'get_all', page, limit: pageSize };
      if (applied.search) body.search = applied.search;
      if (applied.period !== 'all') {
        const from = new Date();
        from.setDate(from.getDate() - Number(applied.period));
        body.date_from = from.toISOString().slice(0, 10);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error(`Error fetching ${channel} messages:`, error);
      toast.error(t('admin.communications.loadError', { channel: label }));
    } finally {
      setLoading(false);
    }
  }, [applied, channel, endpoint, label, page, t]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ search: search.trim(), period });
  };

  const clearFilters = () => {
    setSearch('');
    setPeriod('all');
    setPage(1);
    setApplied({ search: '', period: 'all' });
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      setDeleting(true);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'delete', message_id: messageToDelete.id })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast.success(t('admin.communications.deleted'));
      setMessageToDelete(null);
      await fetchMessages();
    } catch (error) {
      console.error(`Error deleting ${channel} message:`, error);
      toast.error(t('admin.communications.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const senderName = (message: ChannelMessage) => {
    if (channel === 'telegram') {
      return message.telegram_username
        ? `@${message.telegram_username}`
        : message.telegram_user_id
          ? `ID ${message.telegram_user_id}`
          : t('admin.communications.unknown');
    }
    return message.display_name || message.whatsapp_user_id || t('admin.communications.unknown');
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleString(
      i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
      { timeZone: 'Europe/Belgrade', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    );

  const openDetails = (message: ChannelMessage) => {
    navigate(`/admin/${channel}-messages/${message.id}?returnPage=${page}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const filtersActive = Object.values(applied).some((value) => value !== '' && value !== 'all');

  return (
    <div>
      {standalone ? (
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">{label}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.communications.channelDescription', { channel: label })}</p>
        </div>
      ) : null}

      <section className="border-b border-border">
        <div className="flex flex-wrap items-end gap-3 border-b border-border py-4">
          <label className="min-w-60 flex-1">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('admin.communications.search')}</span>
            <span className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && applyFilters()} placeholder={t('admin.communications.searchPlaceholder')} className="h-9 pl-9" />
            </span>
          </label>
          <label className="w-44">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('admin.communications.period')}</span>
            <Select value={period} onValueChange={(value) => setPeriod(value as Period)}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.communications.allTime')}</SelectItem>
                <SelectItem value="7">{t('admin.communications.last7')}</SelectItem>
                <SelectItem value="30">{t('admin.communications.last30')}</SelectItem>
                <SelectItem value="90">{t('admin.communications.last90')}</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <Button className="h-9" onClick={applyFilters}>{t('admin.communications.apply')}</Button>
        </div>

        <div className="flex items-center justify-between gap-3 py-3">
          <p className="text-sm text-muted-foreground">{t('admin.communications.messageCount', { count: total })}</p>
          {filtersActive ? <Button variant="ghost" size="sm" onClick={clearFilters}>{t('admin.communications.clear')}</Button> : null}
        </div>

        <div className="overflow-x-auto pb-2">
          {loading ? (
            <div className="space-y-2 pb-4">{Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)}</div>
          ) : messages.length === 0 ? (
            <div className="flex min-h-56 flex-col items-center justify-center text-center">
              <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="font-medium">{t('admin.communications.noMessages')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{filtersActive ? t('admin.communications.adjustFilters') : t('admin.communications.emptyChannel', { channel: label })}</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('admin.communications.sender')}</TableHead>
                <TableHead className="w-[52%]">{t('admin.communications.message')}</TableHead>
                <TableHead>{t('admin.communications.time')}</TableHead>
                <TableHead className="text-right">{t('admin.communications.actions')}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell className="max-w-[220px] truncate py-4 font-medium" title={senderName(message)}>{senderName(message)}</TableCell>
                    <TableCell className="py-4"><button type="button" onClick={() => openDetails(message)} className="block max-w-[520px] truncate text-left text-sm hover:underline" title={message.user_message}>{message.user_message}</button></TableCell>
                    <TableCell className="whitespace-nowrap py-4 text-sm text-muted-foreground">{formatDate(message.createdAt)}</TableCell>
                    <TableCell className="py-4 text-right"><div className="inline-flex gap-2">
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openDetails(message)} aria-label={t('admin.communications.view')} title={t('admin.communications.view')}><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => setMessageToDelete(message)} aria-label={t('admin.communications.delete')} title={t('admin.communications.delete')}><Trash2 className="h-4 w-4" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {total > 0 ? <div className="flex items-center justify-between gap-3 border-t border-border py-3">
          <p className="text-sm text-muted-foreground">{t('admin.communications.range', { first: (page - 1) * pageSize + 1, last: Math.min(page * pageSize, total), total })}</p>
          <div className="flex items-center gap-2">
            <span className="min-w-16 text-center text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1} aria-label={t('admin.communications.previous')}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={page >= totalPages} aria-label={t('admin.communications.next')}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div> : null}
      </section>

      <AlertDialog open={Boolean(messageToDelete)} onOpenChange={(open) => !open && setMessageToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t('admin.communications.deleteTitle')}</AlertDialogTitle><AlertDialogDescription>{t('admin.communications.deleteDescription')}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.communications.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMessage} disabled={deleting} className="bg-destructive text-destructive-foreground">{deleting ? t('admin.communications.deleting') : t('admin.communications.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChannelMessages;
